/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import * as actionTypes from '../constants/action-types';
import { handleError } from './error';
import { RootState } from '../reducers';
import { ThunkAction } from 'redux-thunk';
import { AnyAction } from 'redux';
import G17Client from '../services/G17Client';
import { ScorecardResponse } from '../types/scorecard';
import { ApiResponse } from '../types/api';

type ScorecardTag = 'main';
type Thunk = ThunkAction<void, RootState, any, AnyAction>;

interface TriggerCheck {
  state: RootState['scorecard'],
  tag: ScorecardTag | undefined;
  sameId: boolean;
  forceRefresh: boolean;
}

const shouldTriggerAction = ({ state, sameId, tag, forceRefresh }: TriggerCheck) => {

  const isLoaded = tag && state[tag] ? state[tag].loaded : state.main.loaded;
  const isLoading = tag && state[tag] ? state[tag].loading : state.main.loading;

  // Diffent Id should always continue
  if (!sameId) {
    return true;
  }

  // Currently loading same id and tag
  if (isLoading) {
    return false;
  }

  // Forcing refresh or fetching new one
  if (forceRefresh) {
    return true;
  }

  // If it's not loaded continue
  return !isLoaded;
}

export function reloadScorecards(): Thunk {
  return (dispatch, getState) => {
    const state = getState().scorecard;
    if (!state.initiativeId) {
      return; // Nothing to refresh
    }
    const surveyId = state.surveyId;
    if (surveyId) {
      return dispatch(loadScorecardsBySurveyId(state.initiativeId, surveyId, false, 'main', true));
    }
    return dispatch(loadScorecardsByInitiativeId(state.initiativeId, false, 'main', true));
  }
}

export function loadScorecardsByInitiativeId(
  initiativeId: string,
  blockingLoad = true,
  tag: ScorecardTag | undefined = undefined,
  forceRefresh = false
): Thunk {
  return (dispatch, getState) => {
    if (!initiativeId) {
      return;
    }

    const state = getState().scorecard;
    const sameId = state.initiativeId === initiativeId;
    if (!shouldTriggerAction({ state, sameId, tag, forceRefresh })) {
      return;
    }

    if (blockingLoad || !sameId) {
      dispatch({
        type: actionTypes.LOADING_SCORECARD,
        initiativeId: initiativeId,
        tag
      });
    }

    return G17Client.get<ApiResponse<ScorecardResponse>>(`scorecard/initiative/${initiativeId}`)
      .then(r => r.data.data)
      .then((scorecard) => {
        return dispatch({
          type: actionTypes.LOADED_SCORECARD,
          data: scorecard,
          initiativeId: initiativeId,
          tag
        });
      })
      .catch(handleError(dispatch, actionTypes.ERROR_SCORECARD))
  }
}

export function loadScorecardsBySurveyId(
  initiativeId: string,
  surveyId: string,
  blockingLoad = true,
  tag: ScorecardTag | undefined = undefined,
  forceRefresh = false,
): Thunk {
  return (dispatch, getState) => {
    if (!initiativeId || !surveyId) {
      return;
    }

    const state = getState().scorecardSurvey;
    const sameId = state.surveyId === surveyId;

    if (!shouldTriggerAction({ state, sameId, tag, forceRefresh })) {
      return;
    }

    if (blockingLoad || !sameId) {
      dispatch({
        type: actionTypes.LOADING_SCORECARD_SURVEY,
        surveyId: surveyId,
        tag,
      });
    }

    return G17Client.get(`scorecard/initiative/${initiativeId}/survey/${surveyId}`, { params: { tag } })
      .then((response) => dispatch({
        type: actionTypes.LOADED_SCORECARD_SURVEY,
        data: response.data.data,
        surveyId: surveyId,
        tag,
      }))
      .catch(handleError(dispatch, actionTypes.ERROR_SCORECARD_SURVEY))
  }
}

export const loadPeerComparison = (initiativeId: string): Thunk => (dispatch, getState) => {
  const state = getState().peerComparison;
  if (state.initiativeId === initiativeId) {
    return;
  }
  dispatch({
    type: actionTypes.LOADING_PEER_COMPARISON,
    initiativeId: initiativeId
  });

  return G17Client.get(`scorecard/initiative/${initiativeId}/comparison`)
    .then((response) => {
      dispatch({
        type: actionTypes.LOADED_PEER_COMPARISON,
        data: response.data.data,
        initiativeId: initiativeId
      });
    })
    .catch(handleError(dispatch, actionTypes.ERROR_PEER_COMPARISON))
};
